import type { ReactNode } from "react";
import {
  Globe,
  BookOpenIcon,
  BotMessageSquare,
  Terminal,
  Wrench,
  Plug,
  Workflow,
  Cog,
  Database,
  User,
  Users,
  ListTodo,
  Brain,
  Code,
  MessageCircle,
  FileText,
  ImagePlus,
  List,
  UserPlus,
  UserMinus,
  ShieldCheck,
  ShieldPlus,
  UserCog,
  Sparkles,
  Clock,
  Zap,
  Table2,
  Building2,
  KeyRound,
  Rocket,
  FlaskConical,
  FileSpreadsheet,
  FolderOpen,
  Inbox,
  Mail,
  type LucideIcon,
} from "lucide-react";
import type { BadgeProps } from "@/components/shadcn/badge";
import { genieTools } from "@/lib/genie/registries/tool-registry";
import { matchMcpTool, type McpPrefixMeta } from "./mcpToolIcon";
import { humanizeToolName } from "@/components/MCP/MCPToolRow";
import type { ToolPartBase } from "./ToolComponents";
import type { CodeModeCall } from "./noxus-message-types";
import {
  decisionWord,
  getApprovalDecision,
  isActionablePendingApproval,
  toolNameFromPartType,
} from "./approval/approvalGate";
import { resolveApproval } from "./approval/approvalRegistry";
import { findDataPart, type MessagePart } from "./utils";

// Backend tool metadata (spot-agents ``v2/tools/metadata.py`` DISPLAYS) names
// its icon as a lucide identifier. This maps that vocabulary to components so a
// tool's icon — like its label — is owned by the backend, never hardcoded per
// tool here. Unknown names fall back to the generic wrench.
export const TOOL_ICON_BY_NAME: Record<string, LucideIcon> = {
  Cog,
  Database,
  BookOpen: BookOpenIcon,
  User,
  Users,
  ListTodo,
  Terminal,
  Brain,
  Globe,
  Code,
  MessageCircle,
  FileText,
  ImagePlus,
  List,
  UserPlus,
  UserMinus,
  ShieldCheck,
  ShieldPlus,
  UserCog,
  Sparkles,
  Clock,
  Workflow,
  Zap,
  BotMessageSquare,
  Table2,
  Building2,
  KeyRound,
  Rocket,
  FlaskConical,
  FileSpreadsheet,
  Plug,
  Inbox,
  Mail,
  FolderOpen,
};

// The backend's TOOL_DISPLAY_DEFAULT messages (spot-agents ``metadata.py``) —
// stamped on any tool no metadata type claims. Treated as "no specific
// display": a tool carrying these uses the FE's own humanized/MCP label.
const DEFAULT_MESSAGE_START = "Running tool...";
const DEFAULT_MESSAGE_END = "Tool complete";

/**
 * Single source of truth for how a tool call is displayed — the chain-of-thought
 * header, the expanded step row, its icon, and whether it folds into a chain all
 * derive from ``resolveToolDisplay``. Adding or retuning a tool's display is a
 * one-line change here instead of edits scattered across chainHeaderText,
 * renderToolPart, and per-tool wrapper components.
 *
 * ``base`` renders through ``BaseTool``; ``workflow`` and ``code`` have rich
 * custom bodies; ``hidden`` renders nothing (the file card is shown elsewhere).
 */
export type ToolRenderer = "workflow" | "code" | "base" | "hidden";

export interface ToolDisplayInfo {
  icon: ReactNode;
  // Scrolling header + row title while the tool runs (message_start) …
  messageStart: string;
  // … once it's done (message_end) …
  messageEnd: string;
  // … and if it errored.
  messageError: string;
  // Optional second line under the title (e.g. a web search's query).
  subtitle?: string;
  // Whether this tool folds into a collapsible chain of thought.
  chainable: boolean;
  // Monospace function-call label in the default preset's row header.
  headerLabel: string;
  renderer: ToolRenderer;
  // The user's approve/deny decision, if the tool went through approval —
  // rendered as a badge on the icon.
  approval?: { approved: boolean; alwaysAllow?: boolean };
}

// Backend paths disagree on the metadata key: BaseToolMetadata emits
// ``display_message_*`` while some v2 tools stamp raw ``tool_display_message_*``.
// Read both so no tool silently loses its label.
interface NoxusToolMeta {
  display_message_start?: string;
  display_message_end?: string;
  tool_display_message_start?: string;
  tool_display_message_end?: string;
  // Imperative label for approval-gated rows ("Build this workflow").
  display_action?: string;
  // What "Always allow" covers ("Deleting agents") and what it acts on.
  display_grant?: string;
  display_area?: ToolArea;
  // The backend (tool metadata + the agent's Display settings) decides which
  // calls never appear in the activity feed.
  hidden?: boolean;
  icon?: string;
  // Rich renderer + subtitle owned by the tool's backend metadata, so a tool's
  // display lives where it's defined instead of a hardcoded branch here.
  display_renderer?: ToolRenderer;
  display_subtitle_arg?: string;
  kb_name?: string;
  agent_name?: string;
  workflow_name?: string;
}

// A call a code mode script made, as the part a direct call of its tool
// would be, so it renders as that tool's row. It already ran: no approval.
export function scriptCallPart(call: CodeModeCall): ToolPartBase {
  return {
    type: `tool-${call.tool_name}`,
    toolCallId: call.tool_call_id,
    state: call.failed ? "output-error" : "output-available",
    input: call.input,
    output: call.output,
    errorText: call.error_text,
    callProviderMetadata: call.provider_metadata,
  };
}

// The value of a named input arg, rendered as a row subtitle (e.g. a web
// search's ``queries``). Arrays join so multi-query calls read cleanly.
function subtitleFromArg(
  input: unknown,
  arg: string | undefined
): string | undefined {
  if (!arg || typeof input !== "object" || input === null) return undefined;
  const value = (input as Record<string, unknown>)[arg];
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(String).join(", ");
  return undefined;
}

function noxusMeta(part: ToolPartBase): NoxusToolMeta | undefined {
  return (part.callProviderMetadata?.noxus ??
    part.resultProviderMetadata?.noxus) as NoxusToolMeta | undefined;
}

// What a gated tool does, as the tool declares it: the backend's
// display_action, or a Genie frontend tool's own.
function declaredAction(part: ToolPartBase): string | undefined {
  return (
    noxusMeta(part)?.display_action ??
    genieTools.get(toolNameFromPartType(part.type))?.display?.action
  );
}

function metaStart(m: NoxusToolMeta | undefined): string | undefined {
  return m?.display_message_start ?? m?.tool_display_message_start;
}

function metaEnd(m: NoxusToolMeta | undefined): string | undefined {
  return m?.display_message_end ?? m?.tool_display_message_end;
}

// "Searching capabilities..." -> "Searching capabilities", to name a failure.
function withoutEllipsis(label: string): string {
  return label.replace(/(\.\.\.|…)$/, "");
}

function baseLabel(type: string): string {
  return type.replace(/^tool-/, "");
}

// Tool families have dynamic suffixes (e.g. ``tool-run_workflow_<hash>``), so a
// family match is an exact hit or ``<key>_<…>``. Looser matching would let
// ``tool-kb`` swallow ``tool-kbase``.
function matchesFamily(type: string, key: string): boolean {
  return type === key || type.startsWith(`${key}_`);
}

// The backend marks bookkeeping calls (todo tools, per the agent's Display
// settings) as hidden — they render nothing, except while one actually awaits
// the user's approval, which must stay visible and actionable.
export function isHiddenToolPart(part: ToolPartBase): boolean {
  return noxusMeta(part)?.hidden === true && !isActionablePendingApproval(part);
}

export function resolveToolDisplay(
  part: ToolPartBase,
  mcpPrefixes?: Map<string, McpPrefixMeta>,
  allParts?: MessagePart[]
): ToolDisplayInfo {
  const info = resolveToolDisplayInner(part, mcpPrefixes, allParts);
  const approval = getApprovalDecision(part);
  if (!approval) return info;
  // Approval-gated calls read as the decision, mirroring the approval card:
  // "Allowed/Always allowed/Rejected · <action>". A denied call never ran, so "… complete"
  // would be a lie; the allowed label keeps the pairing consistent. The
  // action is the tool's declared action, else (for a call with a card) its
  // message, else the tool's monospace header label.
  const toolAction = declaredAction(part);
  const action =
    toolAction ??
    (resolveApproval(part) ? info.messageStart : info.headerLabel);
  return {
    ...info,
    approval,
    messageEnd: `${decisionWord(approval)} · ${action}`,
  };
}

function resolveToolDisplayInner(
  part: ToolPartBase,
  mcpPrefixes?: Map<string, McpPrefixMeta>,
  allParts?: MessagePart[]
): ToolDisplayInfo {
  const type = part.type;
  const m = noxusMeta(part);
  const start = metaStart(m);
  const end = metaEnd(m);

  if (type === "tool-present_file" || isHiddenToolPart(part)) {
    return {
      icon: null,
      messageStart: "",
      messageEnd: "",
      messageError: "",
      chainable: false,
      headerLabel: baseLabel(type),
      renderer: "hidden",
    };
  }

  if (matchesFamily(type, "tool-run_workflow")) {
    const data = allParts
      ? findDataPart(allParts, "data-run_workflow", part.toolCallId)?.data
      : undefined;
    const name = data?.workflow_name ?? m?.workflow_name;
    return {
      icon: <Workflow className="h-4 w-4" />,
      messageStart:
        data?.display_message_start ??
        start ??
        (name ? `Running ${name}` : "Running workflow..."),
      messageEnd:
        data?.display_message_end ??
        end ??
        (name ? `${name} completed` : "Ran workflow"),
      messageError: name ? `Workflow "${name}" failed` : "Workflow failed",
      chainable: true,
      headerLabel: baseLabel(type),
      renderer: "workflow",
    };
  }

  // Code mode's script. Live, the backend stamps its display; a reloaded
  // chat carries only the tool name, so the row is drawn here both ways.
  if (type === "tool-run_code") {
    return {
      icon: <Terminal className="size-4" />,
      messageStart: "Running code...",
      messageEnd: "Code executed",
      messageError: "Code failed",
      chainable: true,
      headerLabel: baseLabel(type),
      renderer: "code",
    };
  }

  if (matchesFamily(type, "tool-call_agent")) {
    const data = allParts
      ? findDataPart(allParts, "data-call_agent", part.toolCallId)?.data
      : undefined;
    const name = data?.agent_name ?? m?.agent_name ?? "Agent";
    return {
      icon: <BotMessageSquare className="h-4 w-4" />,
      messageStart: data?.display_message_start ?? start ?? `Calling "${name}"`,
      messageEnd: data?.display_message_end ?? end ?? `${name} completed`,
      messageError: `Agent "${name}" failed`,
      chainable: true,
      headerLabel: baseLabel(type),
      renderer: "base",
    };
  }

  if (matchesFamily(type, "tool-kb")) {
    const name = m?.kb_name ?? "Knowledge Base";
    return {
      icon: <BookOpenIcon className="h-4 w-4" />,
      messageStart: start ?? `Searching ${name}`,
      messageEnd: end ?? `${name} search complete`,
      messageError: `Search failed for ${name}`,
      chainable: true,
      headerLabel: baseLabel(type),
      renderer: "base",
    };
  }

  // Genie frontend tools define their own display on the registry entry —
  // the registry is only populated inside Genie, so other chats fall through.
  // A specific backend display (not the generic default) still wins.
  const genieDisplay = genieTools.get(toolNameFromPartType(type))?.display;
  if (genieDisplay) {
    const GenieIcon = genieDisplay.icon;
    const input =
      typeof part.input === "object" && part.input !== null
        ? (part.input as Record<string, unknown>)
        : {};
    const backendStart =
      start && start !== DEFAULT_MESSAGE_START ? start : null;
    const backendEnd = end && end !== DEFAULT_MESSAGE_END ? end : null;
    return {
      icon: <GenieIcon className="size-4" />,
      messageStart: backendStart ?? genieDisplay.messageStart(input),
      messageEnd: backendEnd ?? genieDisplay.messageEnd(input),
      messageError: genieDisplay.messageError,
      chainable: true,
      headerLabel: baseLabel(type),
      renderer: "base",
    };
  }

  // MCP tools. A tool a backend metadata type claims (e.g. the genie's workflow
  // tools) carries a specific display — honour its label and icon over the
  // humanized one, so its row, approval card and pill all show one icon. Any
  // other tool keeps the MCP integration logo, so a row still reads as
  // "belongs to <integration>".
  const mcp = mcpPrefixes ? matchMcpTool(type, mcpPrefixes) : null;
  if (mcp) {
    const backendStart =
      start && start !== DEFAULT_MESSAGE_START ? start : null;
    const backendEnd = end && end !== DEFAULT_MESSAGE_END ? end : null;
    const BackendIcon = backendStart && m?.icon && TOOL_ICON_BY_NAME[m.icon];
    const icon = BackendIcon ? (
      <BackendIcon className="size-4" />
    ) : mcp.meta.iconUrl ? (
      <img
        src={mcp.meta.iconUrl}
        alt={mcp.meta.integrationName}
        className="h-4 w-4 rounded-sm object-contain"
      />
    ) : (
      <Plug className="size-4 text-muted-foreground" />
    );
    return {
      icon,
      messageStart: backendStart ?? `Running "${mcp.toolLabel}"`,
      messageEnd: backendEnd ?? `${mcp.toolLabel} complete`,
      messageError: `${backendStart ? withoutEllipsis(backendStart) : mcp.toolLabel} failed`,
      chainable: true,
      headerLabel: `${mcp.meta.integrationName} · ${mcp.toolName}`,
      renderer: "base",
    };
  }

  // Any other tool: honour backend display metadata for both icon and label,
  // else humanize the tool name ("load_skill" → "Load Skill") over the generic
  // wrench. Never fall back to "Thought" here — that's the reasoning group's
  // label, meaningless on a named tool row.
  const label = humanizeToolName(baseLabel(type));
  const Icon = (m?.icon && TOOL_ICON_BY_NAME[m.icon]) || Wrench;
  return {
    icon: <Icon className="size-4" />,
    messageStart: start ?? label,
    messageEnd: end ?? label,
    messageError: `${start ? withoutEllipsis(start) : label} failed`,
    subtitle: subtitleFromArg(part.input, m?.display_subtitle_arg),
    chainable: true,
    headerLabel: baseLabel(type),
    renderer: m?.display_renderer ?? "base",
  };
}

function formatParamValue(v: unknown): string {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}

// Weaves the primary argument into a humanized label ("Search for …").
function buildSentence(label: string, entry: [string, unknown] | null): string {
  if (!entry) return label;
  const value = formatParamValue(entry[1]);
  const lower = label.toLowerCase();
  if (lower.endsWith(" to") || lower.endsWith(" from"))
    return `${label} ${value}`;
  if (/^(get|fetch|load|read|list|create|delete|update)\b/.test(lower))
    return `${label} · ${value}`;
  return `${label} for "${value}"`;
}

// The backend's ToolArea (spot-agents ``metadata.py``). Hues follow the node
// categories (SpotflowV2/nodeCategories.ts); a tool with no area is neutral.
export type ToolArea =
  | "workflows"
  | "tables"
  | "knowledge"
  | "agents"
  | "files"
  | "sandboxes"
  | "cases";

const AREA_VARIANT: Record<ToolArea, BadgeProps["variant"]> = {
  workflows: "color-slate",
  tables: "color-blue",
  knowledge: "color-blue",
  agents: "color-emerald",
  files: "color-sky",
  sandboxes: "color-fuchsia",
  cases: "color-purple",
};

// What "Always allow" covers, as declared by the tool (backend metadata or the
// Genie frontend tool). A tool that declares nothing (one gated by an agent's
// owner) shows its humanized name. ``mcpPrefixes`` gives a third-party MCP
// tool the same integration logo its chain row shows.
export function resolveApprovalGrant(
  part: ToolPartBase,
  mcpPrefixes?: Map<string, McpPrefixMeta>
) {
  const m = noxusMeta(part);
  const toolName = toolNameFromPartType(part.type);
  return {
    label:
      m?.display_grant ??
      genieTools.get(toolName)?.display?.grant ??
      humanizeToolName(toolName),
    icon: resolveToolDisplay(part, mcpPrefixes).icon,
    variant: m?.display_area ? AREA_VARIANT[m.display_area] : "outline",
  };
}

// The header label for an approval-gated tool, shared by the inline message
// card and the composer so the two always read the same. Workflow-editor tools
// already read as an action ("Edit this workflow"); anything else gets its
// primary argument woven in.
export function resolveApprovalLabel(part: ToolPartBase): string {
  // A gated tool's action ("Create a table"), not its running message: the
  // call has not started while it waits.
  const label = declaredAction(part) ?? resolveToolDisplay(part).messageStart;
  if (resolveApproval(part)) return label;
  const inputObj =
    typeof part.input === "object" && part.input !== null
      ? (part.input as Record<string, unknown>)
      : null;
  const primaryEntry = inputObj ? (Object.entries(inputObj)[0] ?? null) : null;
  return buildSentence(label, primaryEntry);
}
